import { Component, input, InputSignal, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';

import { ReferralInputWithRevertComponent } from '../Input-With-Revert/referralInputWithRevert.component';
import { ReferralsService } from '../../referrals.service';
import { ReferralPageService } from '../referralPage.service';

@Component({
    selector: 'referral-client-info',
    templateUrl: 'clientInfo.component.html',
    styleUrls: ['../referralPage.component.scss'],
    imports: [ReactiveFormsModule, ReferralInputWithRevertComponent],
})
export class ReferralClientInfoComponent implements OnInit {
    constructor(
        private readonly _referralService: ReferralsService,
        private readonly _referralPageService: ReferralPageService,
    ) {}
    referral: InputSignal<Core.LitigationReferralModel> = input<Core.LitigationReferralModel>();
    systemReferral: InputSignal<Core.LitigationReferralModel> = input<Core.LitigationReferralModel>();
    originalReferral: InputSignal<Core.LitigationReferralModel> = input<Core.LitigationReferralModel>();
    form: InputSignal<FormGroup> = input<FormGroup>();
    editMode = this._referralPageService.editMode;

    relationshipToPropertyOptions: Weissman.Model.NameValuePair[];

    ngOnInit(): void {
        this.relationshipToPropertyOptions = ['Owner', 'Tenant', 'Manager'].map((x) => ({ name: x, value: x }));
    }

    async onEngagementDataLoaded(engagementID: string, getNewEngagmentData?: boolean): Promise<void> {
        if (!getNewEngagmentData) return;

        const referral = this.referral();
        if (!referral) return;

        const newEngagementData = await this._referralService.getReferralDataForEngagement(engagementID);
        // If a field is null, empty string, or has NOT been overridden by the user (i.e. matches the original referral data),
        // store the looked-up field value.
        // Otherwise, do NOT overwrite it with new engagement data value to preserve the user's overrides.
        if (
            !referral.clientAddress1 ||
            referral.clientAddress1?.trim() === '' ||
            referral.clientAddress1 === this.originalReferral()?.clientAddress1
        ) {
            referral.clientAddress1 = newEngagementData.clientAddress1;
        }
        if (
            !referral.clientAddress2 ||
            referral.clientAddress2?.trim() === '' ||
            referral.clientAddress2 === this.originalReferral()?.clientAddress2
        ) {
            referral.clientAddress2 = newEngagementData.clientAddress2;
        }
        if (
            !referral.clientCity ||
            referral.clientCity?.trim() === '' ||
            referral.clientCity === this.originalReferral()?.clientCity
        ) {
            referral.clientCity = newEngagementData.clientCity;
        }
        if (
            !referral.clientName ||
            referral.clientName?.trim() === '' ||
            referral.clientName === this.originalReferral()?.clientName
        ) {
            referral.clientName = newEngagementData.clientName;
        }
        if (
            !referral.clientState ||
            referral.clientState?.trim() === '' ||
            referral.clientState === this.originalReferral()?.clientState
        ) {
            referral.clientState = newEngagementData.clientState;
        }
        if (
            !referral.clientZip ||
            referral.clientZip?.trim() === '' ||
            referral.clientZip === this.originalReferral()?.clientZip
        ) {
            referral.clientZip = newEngagementData.clientZip;
        }
        if (
            !referral.ryanContactCPName ||
            referral.ryanContactCPName?.trim() === '' ||
            referral.ryanContactCPName === this.originalReferral()?.ryanContactCPName
        ) {
            referral.ryanContactCPName = newEngagementData.ryanContactCPName;
        }
        if (
            !referral.ryanContactEPName ||
            referral.ryanContactEPName?.trim() === '' ||
            referral.ryanContactEPName === this.originalReferral()?.ryanContactEPName
        ) {
            referral.ryanContactEPName = newEngagementData.ryanContactEPName;
        }
        if (
            !referral.clientNumber ||
            referral.clientNumber?.trim() === '' ||
            referral.clientNumber === this.originalReferral()?.clientNumber
        ) {
            referral.clientNumber = newEngagementData.clientNumber;
        }

        this._applyClientFields(referral);
    }

    onEngagementDataReverted(isRevertedToSystem: boolean): void {
        const source = isRevertedToSystem ? this.systemReferral() : this.originalReferral();
        this._applyClientFields(source);
    }

    private _applyClientFields(source: Partial<Core.LitigationReferralModel>): void {
        const referral = this.referral();
        if (!referral || !source) return;

        referral.clientAddress1 = source.clientAddress1;
        referral.clientAddress2 = source.clientAddress2;
        referral.clientCity = source.clientCity;
        referral.clientName = source.clientName;
        referral.clientNumber = source.clientNumber;
        referral.clientState = source.clientState;
        referral.clientZip = source.clientZip;
        referral.ryanContactCPName = source.ryanContactCPName;
        referral.ryanContactEPName = source.ryanContactEPName;

        this.form()?.patchValue({
            clientAddress1: source.clientAddress1,
            clientAddress2: source.clientAddress2,
            clientCity: source.clientCity,
            clientName: source.clientName,
            clientNumber: source.clientNumber,
            clientState: source.clientState,
            clientZip: source.clientZip,
            ryanContactCPName: source.ryanContactCPName,
            ryanContactEPName: source.ryanContactEPName,
        });
    }
}
